// src/context/NetworkContext.jsx
// Provides global network status and pending request queue info.
// `connectivityMode` is the high-level UI signal — see docs/system/offline-mode.md.

import React, { createContext, useState, useEffect, useContext } from 'react';
import { subscribe, getNetworkStatus } from '../utils/api';

const NetworkContext = createContext();

export const useNetwork = () => {
	const context = useContext(NetworkContext);
	if (!context) {
		throw new Error('useNetwork must be used within a NetworkProvider');
	}
	return context;
};

export const NetworkProvider = ({ children }) => {
	const [networkState, setNetworkState] = useState(() => getNetworkStatus());

	useEffect(() => {
		// api.js already listens to window online/offline and pushes status to
		// subscribers. We add direct window listeners as a belt-and-braces in
		// case the browser fires the events before api.js has had a chance to
		// subscribe (e.g. on a cold reload while offline).
		const unsubscribe = subscribe((status) => {
			setNetworkState(status);
		});

		const syncFromCanonical = () => setNetworkState(getNetworkStatus());

		window.addEventListener('online', syncFromCanonical);
		window.addEventListener('offline', syncFromCanonical);

		return () => {
			unsubscribe();
			window.removeEventListener('online', syncFromCanonical);
			window.removeEventListener('offline', syncFromCanonical);
		};
	}, []);

	return (
		<NetworkContext.Provider value={networkState}>
			{children}
		</NetworkContext.Provider>
	);
};

export default NetworkContext;

